import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { CommonDiffEditorOption } from './interface';
import { editor, monacoStore, Monaco } from './monaco-editor';
import { Spin } from '@fx-ui/fine-design';
import ResizeObserver from 'rc-resize-observer';
import classNames from 'classnames';
import { DEFAULT_EDITOR_OPTION } from './constant';
import { observer } from 'mobx-react-lite';

interface DiffEditorOptions {
    className?: string;
    original?: string;
    value?: string;
    editorOption?: CommonDiffEditorOption;
    language: 'javascript' | 'json' | 'python';
    onChange?: (value: string, event: editor.IModelContentChangedEvent) => void;
    onEditorCreate?: (editor: editor.IStandaloneDiffEditor, monaco: Monaco) => void;
    onBeforeEditorDestroy?: (editor: editor.IStandaloneDiffEditor, monaco: Monaco) => void;
}

interface DiffEditorRef {
    instance?: editor.IStandaloneDiffEditor;
}

const DiffEditor: React.ForwardRefRenderFunction<DiffEditorRef, DiffEditorOptions> = (
    {
        original = '',
        value = '',
        editorOption,
        language,
        onBeforeEditorDestroy,
        onChange,
        onEditorCreate,
        className
    },
    ref
) => {
    const { monaco, isLoading } = monacoStore;
    const containerRef = useRef<HTMLDivElement>(null);

    const editorRef = useRef<editor.IStandaloneDiffEditor>();

    useEffect(() => {
        if (containerRef.current && monaco) {
            const { editor } = monaco;
            const editorInstance = editor.createDiffEditor(containerRef.current, {
                ...DEFAULT_EDITOR_OPTION,
                ...editorOption
            });
            editorRef.current = editorInstance;

            const modifiedModel = editor.createModel(value, language);
            const originalModel = editor.createModel(original, language);

            editorInstance.setModel({
                original: originalModel,
                modified: modifiedModel
            });
            onEditorCreate?.(editorInstance, monaco);

            if (onChange) {
                modifiedModel.onDidChangeContent((e) => {
                    onChange(modifiedModel.getValue(), e);
                });
            }

            return () => {
                editorInstance.dispose();
                modifiedModel.dispose();
                originalModel.dispose();
                onBeforeEditorDestroy?.(editorInstance, monaco);
            };
        }
    }, []);

    useEffect(() => {
        editorOption && editorRef.current?.updateOptions(editorOption);
    }, [editorOption]);

    useEffect(() => {
        const { original, modified } = editorRef.current?.getModel() || {};
        if (original && modified && monaco) {
            monaco.editor.setModelLanguage(original, language);
            monaco.editor.setModelLanguage(modified, language);
        }
    }, [language]);

    useEffect(() => {
        const instance = editorRef.current;
        const modifiedModel = instance?.getModel()?.modified;
        if (modifiedModel && instance) {
            instance.getModifiedEditor().pushUndoStop();
            modifiedModel?.pushEditOperations(
                [],
                [
                    {
                        range: modifiedModel.getFullModelRange(),
                        text: value
                    }
                ],
                (prev) => {
                    const lastOp = prev[prev.length - 1];
                    if (lastOp && monaco) {
                        return [monaco.Selection.fromRange(lastOp.range, 0)];
                    }
                    return null;
                }
            );
            instance.getModifiedEditor().pushUndoStop();
        }
    }, [value]);

    useEffect(() => {
        if (editorRef.current) {
            const originalEditor = editorRef.current.getModel()?.original;
            if (originalEditor && original !== originalEditor?.getValue()) {
                originalEditor.setValue(original);
            }
        }
    }, [original]);

    useImperativeHandle(ref, () => ({ instance: editorRef.current }));

    if (isLoading || !monaco) {
        return <Spin size="large" maskType="dark" />;
    }

    return (
        <ResizeObserver
            onResize={() => {
                editorRef.current?.layout();
            }}
        >
            <div ref={containerRef} className={classNames('fx-diff-editor', className)} />
        </ResizeObserver>
    );
};

export default observer(forwardRef(DiffEditor));
